// This file is moved from src/app/country-destination-pages/[id]/page.tsx to src/app/destinations/[id]/page.tsx
// Update internal links to use new /destinations/ path

'use client';

import { useParams } from 'next/navigation';
import Link from 'next/link';
import { useState } from 'react';
import { WithContext, BreadcrumbList } from 'schema-dts';
import Icon from '@/components/ui/AppIcon';
import Image from 'next/image';
import IrelandCostCalculator from '../components/IrelandCostCalculator';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import { generateBreadcrumbSchema } from '@/lib/schemas/breadcrumbSchema';

interface Country {
  id: string;
  name: string;
  flag: string;
  description: string;
  capital: string;
  language: string;
  currency: string;
  currencySymbol: string;
  population: string;
  timezone: string;
  climate: string;
  bestTimeToVisit: string;
  heroImage: string;
  heroImageAlt: string;
  highlights: string[];
  universities: University[];
  visaRequirements: VisaRequirement[];
  costOfLiving: CostOfLiving;
  scholarships: Scholarship[];
  careerOpportunities: string[];
  culturalInsights: string[];
  testimonials: Testimonial[];
  faqs: FAQ[];
}

interface University {
  name: string;
  ranking: string;
  programs: string[];
  tuitionRange: string;
  image: string;
  imageAlt: string;
}

interface VisaRequirement {
  type: string;
  processingTime: string;
  requirements: string[];
}

interface CostOfLiving {
  accommodation: string;
  food: string;
  transportation: string;
  utilities: string;
  entertainment: string;
  total: string;
}

interface Scholarship {
  name: string;
  amount: string;
  eligibility: string;
}

interface Testimonial {
  name: string;
  university: string;
  quote: string;
  image: string;
  imageAlt: string;
}

interface FAQ {
  question: string;
  answer: string;
}

const countryData: Record<string, Country> = {
  dubai: {
    id: 'dubai',
    name: 'Dubai, UAE',
    flag: '🇦🇪',
    description: 'Experience world-class education in the heart of innovation and luxury. Dubai offers a unique blend of modern infrastructure, multicultural environment, and growing academic excellence with over 70 accredited higher education institutions.',
    capital: 'Abu Dhabi',
    language: 'Arabic, English',
    currency: 'UAE Dirham',
    currencySymbol: 'AED',
    population: '3.6 million',
    timezone: 'GST (UTC+4)',
    climate: 'Hot desert climate with mild winters',
    bestTimeToVisit: 'November to March',
    heroImage: '/assets/images/Dubai-1768284035434.jpg',
    heroImageAlt: 'Dubai skyline featuring Burj Khalifa and modern architecture at sunset',
    highlights: [
    'Tax-free income and generous scholarships',
    'State-of-the-art campus facilities',
    'Gateway to Middle East and Asia',
    'Diverse international community of 200+ nationalities',
    'Post-study work opportunities via Golden Visa',
    'Safe and modern infrastructure ranked top globally'],

    universities: [
    {
      name: 'American University in Dubai (AUD)',
      ranking: 'Top 5 in UAE – SACSCOC & CAA Accredited',
      programs: ['Business Administration', 'Engineering', 'Architecture', 'Communication & Media'],
      tuitionRange: 'AED 72,000 – AED 95,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1bd0acd09-1772955342762.png',
      imageAlt: 'Modern university campus building with glass facade and students walking'
    },
    {
      name: 'Heriot-Watt University Dubai',
      ranking: 'UK Top 50 – Times Higher Education 2025',
      programs: ['Engineering', 'Business', 'Computer Science', 'Psychology'],
      tuitionRange: 'AED 68,000 – AED 88,000/year',
      image: 'https://images.unsplash.com/photo-1582939224887-986825635643',
      imageAlt: 'University lecture hall with modern seating and presentation screen'
    },
    {
      name: 'Middlesex University Dubai',
      ranking: 'UK Degree – Internationally Recognised',
      programs: ['Business', 'Law', 'Media', 'IT & Computing'],
      tuitionRange: 'AED 58,000 – AED 78,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16fb3ceb8-1769245706130.png',
      imageAlt: 'Students collaborating in modern university study space'
    }],

    visaRequirements: [
    {
      type: 'UAE Student Visa (Residence Permit)',
      processingTime: '2–4 weeks',
      requirements: [
      'Valid passport (minimum 6 months validity)',
      'Original acceptance letter from a KHDA-approved institution',
      'Proof of financial support (AED 50,000+ or bank statement)',
      'Medical fitness certificate (blood test + chest X-ray)',
      'Attested educational certificates and transcripts',
      'Passport-sized photographs (white background)',
      'Emirates ID application (biometrics required on arrival)',
      'Health insurance (mandatory – minimum AED 600/year)']

    }],

    costOfLiving: {
      accommodation: 'AED 2,800 – AED 5,500/month',
      food: 'AED 1,000 – AED 1,800/month',
      transportation: 'AED 300 – AED 600/month (RTA Metro/Bus)',
      utilities: 'AED 500 – AED 900/month',
      entertainment: 'AED 500 – AED 1,200/month',
      total: 'AED 5,100 – AED 10,000/month'
    },
    scholarships: [
    {
      name: 'Mohammed Bin Rashid Scholarship (MBRS)',
      amount: 'Full tuition + AED 2,000/month stipend',
      eligibility: 'Outstanding academic merit (GPA 3.7+)'
    },
    {
      name: 'University Merit Scholarships',
      amount: '25–50% tuition waiver',
      eligibility: 'High GPA and entrance exam scores'
    },
    {
      name: 'MBZUAI Graduate Scholarship',
      amount: 'Full tuition + AED 15,000+/month stipend',
      eligibility: 'MSc/PhD students in AI-related fields'
    }],

    careerOpportunities: [
    'Thriving job market in finance, technology, and hospitality',
    'Tax-free salary packages – no income tax',
    'Networking with 500+ Fortune 500 regional HQs',
    'Post-study Golden Visa for exceptional graduates',
    'Gateway to Middle East & African career opportunities',
    'Entrepreneurship-friendly free zones (DIFC, DMCC)'],

    culturalInsights: [
    'Respect local customs and dress modestly in public areas',
    'Friday–Saturday is the weekend; Sunday is a working day',
    'Ramadan observance: no eating/drinking in public during fasting hours',
    'Diverse expat community from 200+ nationalities',
    'English widely spoken in business and education',
    'Rich blend of traditional Emirati and modern cosmopolitan culture'],

    testimonials: [
    {
      name: 'Priya Sharma',
      university: 'American University in Dubai',
      quote: 'Studying in Dubai opened doors I never imagined. The multicultural environment and career opportunities are unmatched.',
      image: 'https://images.unsplash.com/photo-1636003828223-cb825480325f',
      imageAlt: 'Portrait of smiling female student in graduation attire'
    },
    {
      name: 'Ahmed Al-Mansoori',
      university: 'Heriot-Watt University Dubai',
      quote: 'World-class education with the comfort of home. Dubai provides the perfect balance of academic excellence and quality of life.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_13a9a524b-1763296756971.png',
      imageAlt: 'Portrait of confident male student in business casual attire'
    }],

    faqs: [
    {
      question: 'Can I work while studying in Dubai?',
      answer: 'Yes, international students can work part-time (up to 20 hours/week) with a No Objection Certificate (NOC) from their university. Many universities have career centers that help students find internships and part-time opportunities.'
    },
    {
      question: 'What is the cost of living compared to other study destinations?',
      answer: "Dubai's cost of living is moderate compared to Western countries. While accommodation can be expensive, the tax-free income and scholarships often offset costs. Students can find affordable housing in shared accommodations starting from AED 1,500/month."
    },
    {
      question: 'Is Dubai safe for international students?',
      answer: 'Dubai is consistently ranked among the top 10 safest cities globally, with very low crime rates. The government prioritizes safety and security, making it an ideal destination for international students.'
    },
    {
      question: 'What language do I need to know?',
      answer: 'English is the primary language of instruction in universities and widely spoken in Dubai. While Arabic is the official language, knowing it is not mandatory for daily life or studies.'
    },
    {
      question: 'Are there post-study work opportunities?',
      answer: 'Yes, the UAE offers the Golden Visa (5–10 years) for exceptional graduates and a Job Seeker Visa (90 days) for graduates from top 500 global universities. Many multinational companies in Dubai actively recruit from local universities.'
    }]

  },
  germany: {
    id: 'germany',
    name: 'Germany',
    flag: '🇩🇪',
    description: "Study in Europe's economic powerhouse with tuition-free education at world-renowned universities. Germany combines academic excellence with rich cultural heritage and strong career prospects, hosting over 400,000 international students annually.",
    capital: 'Berlin',
    language: 'German, English (in many programs)',
    currency: 'Euro',
    currencySymbol: '€',
    population: '84 million',
    timezone: 'CET (UTC+1)',
    climate: 'Temperate seasonal climate',
    bestTimeToVisit: 'May to September',
    heroImage: 'https://images.unsplash.com/photo-1730122655558-51f71997c910',
    heroImageAlt: 'Brandenburg Gate in Berlin with German flag colors illuminated at dusk',
    highlights: [
    'Tuition-free education at public universities (only ~€350 semester fee)',
    'World-class engineering and research programs',
    'Strong economy – 4th largest globally',
    '18-month post-study job-search visa',
    'Central location in Europe',
    'High quality of life and safety'],

    universities: [
    {
      name: 'Technical University of Munich (TUM)',
      ranking: '#37 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Computer Science', 'Natural Sciences', 'Medicine'],
      tuitionRange: '€0 (public) + €144 semester fee',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_12f4fff37-1770791815456.png',
      imageAlt: 'Historic university building with classical architecture and students on campus'
    },
    {
      name: 'Ludwig Maximilian University (LMU Munich)',
      ranking: '#59 Global – QS World Rankings 2025',
      programs: ['Medicine', 'Law', 'Business', 'Humanities'],
      tuitionRange: '€0 (public) + €144 semester fee',
      image: 'https://images.unsplash.com/photo-1615889944278-3aab1d57d543',
      imageAlt: 'Students studying together in modern university library'
    },
    {
      name: 'Heidelberg University',
      ranking: '#63 Global – QS World Rankings 2025',
      programs: ['Medicine', 'Life Sciences', 'Law', 'Humanities'],
      tuitionRange: '€0 (public) + €180 semester fee',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1690de774-1772133511024.png',
      imageAlt: 'Traditional university lecture hall with wooden benches and ornate ceiling'
    }],

    visaRequirements: [
    {
      type: 'German Student Visa (National Visa – Type D)',
      processingTime: '6–12 weeks (apply early)',
      requirements: [
      'Valid passport (valid for the entire study period)',
      'University admission letter (Zulassungsbescheid)',
      'Proof of financial resources: €11,904/year in a blocked account (Sperrkonto) – updated 2024',
      'Health insurance coverage (public or private)',
      'German language proficiency (TestDaF/DSH for German-taught programs)',
      'Attested educational certificates and transcripts',
      'Motivation letter (Motivationsschreiben)',
      'Completed visa application form (2 copies)']

    }],

    costOfLiving: {
      accommodation: '€350 – €800/month',
      food: '€200 – €350/month',
      transportation: '€29/month (Deutschlandticket for students)',
      utilities: '€100 – €150/month',
      entertainment: '€100 – €200/month',
      total: '€779 – €1,500/month'
    },
    scholarships: [
    {
      name: 'DAAD Scholarships',
      amount: '€934 – €1,200/month + health insurance',
      eligibility: 'Academic excellence and research potential'
    },
    {
      name: 'Deutschlandstipendium',
      amount: '€300/month',
      eligibility: 'High academic achievement and social engagement'
    },
    {
      name: 'Erasmus+ Program',
      amount: '€300 – €700/month (varies by country)',
      eligibility: 'EU and partner country students'
    }],

    careerOpportunities: [
    'Strong engineering and technology sectors (BMW, Siemens, SAP)',
    '18-month post-study visa for job search',
    'Competitive starting salaries: €45,000 – €60,000/year for graduates',
    'EU Blue Card pathway to permanent residency in 21–33 months',
    'Opportunity Card (Chancenkarte) for skilled workers',
    'Access to entire European job market'],

    culturalInsights: [
    'Punctuality is highly valued – be on time',
    'Direct communication style',
    'Strong work-life balance culture',
    'Rich history and cultural heritage',
    'Excellent public transportation (Deutschlandticket)',
    'Environmental consciousness and sustainability focus'],

    testimonials: [
    {
      name: 'Rajesh Kumar',
      university: 'Technical University of Munich',
      quote: "Germany's tuition-free education and world-class engineering programs made my dream affordable. The research opportunities are incredible.",
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_14ff30f05-1763294027584.png',
      imageAlt: 'Portrait of male engineering student with laptop'
    },
    {
      name: 'Maria Santos',
      university: 'Heidelberg University',
      quote: 'The quality of education and the international environment exceeded my expectations. Germany is perfect for ambitious students.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c440ad12-1765286115178.png',
      imageAlt: 'Portrait of female student in university setting'
    }],

    faqs: [
    {
      question: 'Do I need to speak German to study in Germany?',
      answer: "Many Master's programs are offered in English. However, learning German is highly recommended for daily life and significantly improves job prospects. Most universities offer free German language courses."
    },
    {
      question: 'Is education really free in Germany?',
      answer: 'Yes, most public universities charge no tuition fees for undergraduate and many graduate programs. You only pay a semester contribution (€144–€350) which often includes a public transportation pass.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'Yes, international students can work 120 full days or 240 half days per year. Many students find part-time jobs or working student positions (Werkstudent) in their field, earning €12–€20/hour.'
    },
    {
      question: 'What are the job prospects after graduation?',
      answer: 'Germany has a strong economy with high demand for skilled workers, especially in engineering, IT, and healthcare. The 18-month post-study work visa gives ample time to find employment.'
    },
    {
      question: 'How much money do I need in a blocked account?',
      answer: 'As of 2024, you need €11,904 per year (€992/month) in a blocked account (Sperrkonto) to prove financial resources for your visa. This amount is released monthly to cover living expenses.'
    }]

  },
  singapore: {
    id: 'singapore',
    name: 'Singapore',
    flag: '🇸🇬',
    description: "Experience world-class education in Asia's leading education hub. Singapore offers top-ranked universities, a multicultural environment, and excellent career opportunities in a safe, modern city-state attracting over 90,000 international students.",
    capital: 'Singapore',
    language: 'English, Mandarin, Malay, Tamil',
    currency: 'Singapore Dollar',
    currencySymbol: 'S$',
    population: '5.9 million',
    timezone: 'SGT (UTC+8)',
    climate: 'Tropical climate, warm and humid year-round',
    bestTimeToVisit: 'February to April',
    heroImage: 'https://images.unsplash.com/photo-1627196380765-54b657304550',
    heroImageAlt: 'Modern Singapore skyline with Marina Bay Sands hotel and Gardens by the Bay at sunset',
    highlights: [
    'NUS & NTU ranked Top 15 globally (QS 2025)',
    'English as primary language of instruction',
    'Gateway to Asian business opportunities',
    'Safe and efficient living environment',
    'Multicultural society with 4 official languages',
    'Strong job market and innovation hub'],

    universities: [
    {
      name: 'National University of Singapore (NUS)',
      ranking: '#8 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Business', 'Computer Science', 'Medicine'],
      tuitionRange: 'S$17,550 – S$39,000/year (with tuition grant)',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_13634236c-1767003313995.png',
      imageAlt: 'Modern university campus with contemporary architecture'
    },
    {
      name: 'Nanyang Technological University (NTU)',
      ranking: '#15 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Business', 'Science', 'Humanities'],
      tuitionRange: 'S$17,100 – S$36,000/year (with tuition grant)',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16fb3ceb8-1769245706130.png',
      imageAlt: 'University students collaborating in modern learning space'
    },
    {
      name: 'Singapore Management University (SMU)',
      ranking: 'Top Business School in Asia – QS 2025',
      programs: ['Business', 'Economics', 'Law', 'Information Systems'],
      tuitionRange: 'S$20,000 – S$42,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1067ac00f-1764679554341.png',
      imageAlt: 'Business school students in modern classroom'
    }],

    visaRequirements: [
    {
      type: "Student's Pass (STP) – ICA Singapore",
      processingTime: '2–4 weeks (via SOLAR system)',
      requirements: [
      'Valid passport (minimum 6 months validity)',
      'Acceptance letter from MOE-registered institution',
      'Completed eForm 16 (Student Pass application)',
      'Recent passport-sized photograph',
      'Proof of financial ability (S$10,000+ per year)',
      'Attested educational certificates and transcripts',
      'Medical examination report (if required)',
      'Disembarkation/Embarkation (D/E) card']

    }],

    costOfLiving: {
      accommodation: 'S$700 – S$1,800/month (on-campus S$400–S$800)',
      food: 'S$300 – S$600/month (hawker centres S$3–S$6/meal)',
      transportation: 'S$80 – S$150/month (MRT/Bus)',
      utilities: 'S$50 – S$100/month',
      entertainment: 'S$200 – S$400/month',
      total: 'S$1,330 – S$3,050/month'
    },
    scholarships: [
    {
      name: 'Singapore Government Scholarship (MOE Tuition Grant)',
      amount: 'Up to 90% tuition subsidy',
      eligibility: 'International students who commit to work in Singapore for 3 years post-graduation'
    },
    {
      name: 'ASEAN Undergraduate Scholarship',
      amount: 'Full tuition + S$5,800/year allowance',
      eligibility: 'ASEAN nationals with excellent academics'
    },
    {
      name: 'NUS/NTU Merit Scholarships',
      amount: 'S$10,000 – S$20,000/year',
      eligibility: 'High academic achievement (top 5% of cohort)'
    }],

    careerOpportunities: [
    'Thriving financial and technology sectors (Fintech hub of Asia)',
    'Regional headquarters of 7,000+ multinational companies',
    'Competitive salaries: S$3,500 – S$6,000/month for fresh graduates',
    'Employment Pass and S Pass for post-study work',
    'Gateway to Southeast Asian markets',
    'Innovation and startup ecosystem (Block71)'],

    culturalInsights: [
    'Multicultural society with Chinese, Malay, Indian, and Eurasian communities',
    'English widely spoken in business and education',
    'Clean and green city with strict regulations (no littering, no chewing gum)',
    'Efficient MRT public transportation system',
    'Delicious and diverse cuisine at affordable hawker centres',
    'Respect for different religions and customs'],

    testimonials: [
    {
      name: 'Wei Chen',
      university: 'National University of Singapore',
      quote: 'Singapore offers the perfect blend of Asian culture and global education standards. The career opportunities are exceptional.',
      image: 'https://images.unsplash.com/photo-1726101163785-79618c28f8fa',
      imageAlt: 'Portrait of smiling student in graduation attire'
    },
    {
      name: 'Aisha Rahman',
      university: 'Nanyang Technological University',
      quote: 'The quality of education and the international environment exceeded my expectations. Singapore is truly Asia\'s education hub.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_13a9a524b-1763296756971.png',
      imageAlt: 'Portrait of confident student in business casual attire'
    }],

    faqs: [
    {
      question: "Can I work while studying in Singapore?",
      answer: "Yes, Student's Pass holders can work up to 16 hours per week during term time without additional approval. During vacation periods, you can work full-time."
    },
    {
      question: 'Is Singapore expensive for students?',
      answer: 'While Singapore has a higher cost of living compared to some Asian countries, affordable options exist. On-campus housing and hawker centre meals (S$3–S$6) keep costs manageable. The MOE Tuition Grant significantly reduces tuition fees.'
    },
    {
      question: 'What language is used for instruction?',
      answer: 'English is the primary language of instruction in all universities. This makes Singapore an ideal destination for international students.'
    },
    {
      question: 'Are there post-study work opportunities?',
      answer: 'Yes, graduates can apply for an Employment Pass (minimum S$5,000/month salary) or S Pass (minimum S$3,150/month). Singapore actively attracts global talent, especially in technology, finance, and engineering sectors.'
    },
    {
      question: 'Is Singapore safe for international students?',
      answer: 'Singapore is consistently ranked among the top 3 safest countries in the world with very low crime rates. The government maintains strict law and order, making it ideal for students.'
    }]

  },
  france: {
    id: 'france',
    name: 'France',
    flag: '🇫🇷',
    description: 'Immerse yourself in art, culture, and academic excellence. France offers affordable education at prestigious universities, rich cultural heritage, and a central location for exploring Europe, attracting over 400,000 international students annually.',
    capital: 'Paris',
    language: 'French, English (in many programs)',
    currency: 'Euro',
    currencySymbol: '€',
    population: '68 million',
    timezone: 'CET (UTC+1)',
    climate: 'Temperate climate with mild winters',
    bestTimeToVisit: 'April to June, September to October',
    heroImage: 'https://images.unsplash.com/photo-1714301283268-c2aa3292b19f',
    heroImageAlt: 'Eiffel Tower in Paris with Seine River and historic buildings during golden hour',
    highlights: [
    'Very low tuition at public universities (€170–€3,770/year)',
    'Over 1,700 English-taught programs available',
    'Rich cultural and artistic heritage',
    'Strong programs in arts, business, and engineering',
    'Post-study APS visa (up to 2 years)',
    'High quality of life – ranked #1 in healthcare'],

    universities: [
    {
      name: 'Sorbonne University',
      ranking: '#59 Global – QS World Rankings 2025',
      programs: ['Arts', 'Humanities', 'Sciences', 'Medicine'],
      tuitionRange: '€170 – €3,770/year (public)',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_185f7a1d2-1769651706881.png',
      imageAlt: 'Historic Sorbonne University building with classical architecture'
    },
    {
      name: 'École Polytechnique (l\'X)',
      ranking: '#61 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Mathematics', 'Physics', 'Computer Science'],
      tuitionRange: '€0 – €15,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1fd8753e9-1769361223219.png',
      imageAlt: 'Modern engineering laboratory with students'
    },
    {
      name: 'HEC Paris',
      ranking: '#1 Business School in Europe – FT Rankings 2024',
      programs: ['Business', 'Management', 'Finance', 'Marketing'],
      tuitionRange: '€16,500 – €42,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d0743cd4-1767083174104.png',
      imageAlt: 'Business school campus with modern facilities'
    }],

    visaRequirements: [
    {
      type: 'Long-Stay Student Visa (VLS-TS Étudiant)',
      processingTime: '4–6 weeks (via Campus France)',
      requirements: [
      'Valid passport (valid for entire study period)',
      'University acceptance letter',
      'Proof of financial resources (€615/month – minimum)',
      'Health insurance coverage (CPAM or private)',
      'Accommodation proof (university housing or rental)',
      'Academic transcripts (attested)',
      'Campus France approval (mandatory for most countries)',
      'Completed visa application form + photos']

    }],

    costOfLiving: {
      accommodation: '€400 – €900/month (CROUS student housing from €200)',
      food: '€200 – €350/month (university canteen from €3.30/meal)',
      transportation: '€75/month (student Navigo pass in Paris)',
      utilities: '€100 – €150/month',
      entertainment: '€100 – €200/month',
      total: '€875 – €1,600/month'
    },
    scholarships: [
    {
      name: 'Eiffel Excellence Scholarship (Campus France)',
      amount: '€1,181 – €1,400/month + benefits',
      eligibility: "Outstanding international students at Master's/PhD level"
    },
    {
      name: 'Charpak Scholarship (India-specific)',
      amount: '€700 – €1,000/month',
      eligibility: 'Indian students for Bachelor/Master/Research internships'
    },
    {
      name: 'Erasmus+ Program',
      amount: '€300 – €700/month (varies by country)',
      eligibility: 'EU and partner country students'
    }],

    careerOpportunities: [
    'Strong luxury goods, fashion, and aerospace industries',
    'Growing technology and startup ecosystem (Station F – world\'s largest startup campus)',
    'Post-study APS visa: 1 year (Bachelor) / 2 years (Master+)',
    'Access to European job market',
    'Opportunities in aerospace (Airbus, Safran) and automotive',
    'Cultural and creative industries'],

    culturalInsights: [
    'Rich artistic and cultural heritage – 1,200+ museums',
    'World-renowned cuisine and wine culture',
    'Learning French enhances experience and career prospects',
    'Work-life balance highly valued (35-hour work week)',
    'Excellent public transportation (TGV, Metro)',
    'Central location for European travel'],

    testimonials: [
    {
      name: 'Sophie Martin',
      university: 'Sorbonne University',
      quote: 'Studying in France was a dream come true. The combination of academic excellence and cultural richness is unmatched.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_175f1d87d-1772737019852.png',
      imageAlt: 'Portrait of female student in university setting'
    },
    {
      name: 'Ahmed Hassan',
      university: 'École Polytechnique',
      quote: 'The quality of engineering education and research opportunities in France exceeded my expectations. Plus, the affordable tuition made it possible.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_14ff30f05-1763294027584.png',
      imageAlt: 'Portrait of male engineering student with laptop'
    }],

    faqs: [
    {
      question: 'Do I need to speak French to study in France?',
      answer: "Many programs, especially at Master's level, are offered in English (1,700+ programs). However, learning French is highly recommended for daily life and significantly enhances your experience and job prospects."
    },
    {
      question: 'How affordable is education in France?',
      answer: 'Public universities in France charge very low tuition fees (€170–€3,770/year) for most programs. CROUS student housing starts from €200/month. University canteen meals cost just €3.30.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'Yes, international students can work up to 964 hours per year (approximately 20 hours per week). The minimum wage (SMIC) is €11.88/hour (2024).'
    },
    {
      question: 'What are the post-study work options?',
      answer: "Graduates can apply for an APS (Autorisation Provisoire de Séjour): 1 year for Bachelor's graduates, 2 years for Master's and PhD graduates. This allows you to work or seek employment in France."
    },
    {
      question: 'Is Paris the only option for studying?',
      answer: 'No, France has excellent universities across the country. Cities like Lyon, Toulouse, Bordeaux, and Marseille offer quality education with 20–30% lower living costs than Paris.'
    }]

  },
  russia: {
    id: 'russia',
    name: 'Russia',
    flag: '🇷🇺',
    description: "Study in the world's largest country with affordable quality education. Russia offers strong programs in medicine, engineering, and sciences, combined with rich cultural heritage and history, attracting over 350,000 international students annually.",
    capital: 'Moscow',
    language: 'Russian, English (in some programs)',
    currency: 'Russian Ruble',
    currencySymbol: '₽',
    population: '144 million',
    timezone: 'Multiple zones (MSK UTC+3 in Moscow)',
    climate: 'Continental climate with cold winters',
    bestTimeToVisit: 'May to September',
    heroImage: 'https://images.unsplash.com/photo-1648854751027-04266eea061c',
    heroImageAlt: 'Saint Basil Cathedral in Moscow Red Square with colorful onion domes against blue sky',
    highlights: [
    'Very affordable tuition: ₽150,000 – ₽400,000/year',
    'Strong programs in medicine, engineering, and sciences',
    'Russian Government Scholarship (15,000 seats/year)',
    'Rich cultural and historical heritage',
    'WHO-recognized medical degrees',
    'Diverse landscapes and experiences'],

    universities: [
    {
      name: 'Lomonosov Moscow State University (MSU)',
      ranking: '#87 Global – QS World Rankings 2025',
      programs: ['Sciences', 'Medicine', 'Engineering', 'Humanities'],
      tuitionRange: '₽250,000 – ₽450,000/year',
      image: 'https://images.unsplash.com/photo-1631887273574-30d17472fa51',
      imageAlt: 'Historic university building with classical Soviet architecture'
    },
    {
      name: 'Saint Petersburg State University (SPbU)',
      ranking: '#242 Global – QS World Rankings 2025',
      programs: ['International Relations', 'Languages', 'Sciences', 'Law'],
      tuitionRange: '₽200,000 – ₽380,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_122de6332-1772133541858.png',
      imageAlt: 'University campus with neoclassical architecture'
    },
    {
      name: 'RUDN University (Peoples\' Friendship University)',
      ranking: 'Top 100 in BRICS – QS 2025',
      programs: ['Medicine', 'Engineering', 'Economics', 'Languages'],
      tuitionRange: '₽180,000 – ₽350,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_15676a22b-1766490541638.png',
      imageAlt: 'Modern university lecture hall with students'
    }],

    visaRequirements: [
    {
      type: 'Russian Student Visa (Category D)',
      processingTime: '3–5 weeks',
      requirements: [
      'Valid passport (minimum 18 months validity)',
      'Original invitation letter from Russian university (issued by Ministry of Internal Affairs)',
      'HIV test certificate (mandatory)',
      'Medical certificate (general health)',
      'Attested educational certificates with apostille',
      'Passport-sized photographs (3.5×4.5 cm)',
      'Completed visa application form',
      'Proof of financial support']

    }],

    costOfLiving: {
      accommodation: '₽8,000 – ₽25,000/month',
      food: '₽10,000 – ₽18,000/month',
      transportation: '₽2,000 – ₽4,000/month',
      utilities: '₽3,000 – ₽6,000/month',
      entertainment: '₽5,000 – ₽10,000/month',
      total: '₽28,000 – ₽63,000/month'
    },
    scholarships: [
    {
      name: 'Russian Government Scholarship (Rossotrudnichestvo)',
      amount: 'Full tuition + ₽1,500 – ₽2,800/month stipend',
      eligibility: 'International students with strong academics (15,000 seats/year)'
    },
    {
      name: 'Open Doors Scholarship',
      amount: "Full tuition for Master's/PhD",
      eligibility: 'High achievers in international Olympiads'
    },
    {
      name: 'University Merit Scholarships',
      amount: 'Partial to full tuition',
      eligibility: 'Academic excellence'
    }],

    careerOpportunities: [
    'Growing technology and IT sectors',
    'Opportunities in energy and natural resources',
    'Medical graduates highly sought after internationally',
    'Engineering roles in various industries',
    'Teaching opportunities for language graduates',
    'International companies operating in Russia'],

    culturalInsights: [
    'Rich literary and artistic traditions (Tolstoy, Dostoevsky)',
    'Learning Russian opens many opportunities',
    'Warm hospitality despite cold climate',
    'Diverse cultural experiences across 11 time zones',
    'Ballet, opera, and classical music heritage',
    'Traditional cuisine and customs'],

    testimonials: [
    {
      name: 'Priya Patel',
      university: 'Lomonosov Moscow State University',
      quote: 'Russia offered me quality medical education at an affordable cost. The experience has been enriching both academically and culturally.',
      image: 'https://images.unsplash.com/photo-1623854766500-6a240e7cbee2',
      imageAlt: 'Portrait of smiling medical student'
    },
    {
      name: 'Carlos Rodriguez',
      university: 'Saint Petersburg State University',
      quote: 'Studying in Russia was an adventure. The education quality is excellent, and the cultural experience is unforgettable.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_160126dd8-1773178814596.png',
      imageAlt: 'Portrait of confident male student'
    }],

    faqs: [
    {
      question: 'Do I need to speak Russian to study in Russia?',
      answer: 'Many universities offer programs in English, especially in medicine and engineering. However, learning Russian is beneficial for daily life and cultural integration. A preparatory Russian language year is often available.'
    },
    {
      question: 'How cold does it get in Russia?',
      answer: 'Winters can be very cold, especially in Moscow and northern cities (-10°C to -30°C). However, buildings are well-heated, and you\'ll adapt with proper winter clothing.'
    },
    {
      question: 'Is Russian education recognized internationally?',
      answer: 'Yes, Russian degrees, especially in medicine and engineering, are recognized worldwide. Medical degrees are listed in the WHO World Directory of Medical Schools.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'International students can work part-time with permission from their university and migration authorities. On-campus work is generally easier to obtain.'
    },
    {
      question: 'How affordable is Russia for students?',
      answer: 'Russia is one of the most affordable study destinations. Tuition fees range from ₽150,000–₽450,000/year ($1,600–$5,000), and living costs are significantly lower than Western Europe or North America.'
    }]

  },
  china: {
    id: 'china',
    name: 'China',
    flag: '🇨🇳',
    description: "Experience education in the world's rising superpower. China offers generous scholarships, rapidly growing universities, and the opportunity to learn Mandarin while experiencing ancient culture meets modern innovation. Over 500,000 international students study in China.",
    capital: 'Beijing',
    language: 'Mandarin, English (in many programs)',
    currency: 'Chinese Yuan (Renminbi)',
    currencySymbol: '¥',
    population: '1.4 billion',
    timezone: 'CST (UTC+8)',
    climate: 'Varies by region, generally continental',
    bestTimeToVisit: 'April to May, September to October',
    heroImage: 'https://images.unsplash.com/photo-1644546971385-83fa1d539a3e',
    heroImageAlt: 'Great Wall of China winding through green mountains under clear blue sky',
    highlights: [
    'Chinese Government Scholarship (CSC) – 50,000+ awards/year',
    'Tsinghua & Peking University in Top 25 globally (QS 2025)',
    'Learn Mandarin in immersive environment',
    'Ancient culture meets modern technology',
    'Affordable tuition: ¥15,000 – ¥40,000/year',
    'World-class infrastructure and research facilities'],

    universities: [
    {
      name: 'Tsinghua University',
      ranking: '#20 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Computer Science', 'Business', 'Sciences'],
      tuitionRange: '¥26,000 – ¥45,000/year',
      image: 'https://images.unsplash.com/photo-1641879609693-1a4bc94a5c8b',
      imageAlt: 'Modern university campus with traditional Chinese architecture elements'
    },
    {
      name: 'Peking University (PKU)',
      ranking: '#17 Global – QS World Rankings 2025',
      programs: ['Humanities', 'Sciences', 'Medicine', 'Business'],
      tuitionRange: '¥26,000 – ¥45,000/year',
      image: 'https://images.unsplash.com/photo-1694881186423-5d8783a21de2',
      imageAlt: 'Historic university building with traditional Chinese garden'
    },
    {
      name: 'Fudan University',
      ranking: '#39 Global – QS World Rankings 2025',
      programs: ['Business', 'Medicine', 'Engineering', 'International Studies'],
      tuitionRange: '¥23,000 – ¥38,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_19a121da7-1764641104290.png',
      imageAlt: 'University students in modern learning environment'
    }],

    visaRequirements: [
    {
      type: 'X1/X2 Student Visa (Study Visa)',
      processingTime: '4–6 weeks',
      requirements: [
      'Valid passport (minimum 6 months validity)',
      'JW201 (scholarship) or JW202 (self-funded) form from university',
      'Admission notice (录取通知书) from Chinese university',
      'Physical examination record (using Chinese health form)',
      'Proof of financial support (¥20,000+ per year)',
      'Passport-sized photographs (white background)',
      'Study plan or research proposal',
      'No criminal record certificate']

    }],

    costOfLiving: {
      accommodation: '¥1,500 – ¥3,500/month (on-campus ¥800–¥2,000)',
      food: '¥1,000 – ¥2,000/month',
      transportation: '¥200 – ¥500/month',
      utilities: '¥200 – ¥500/month',
      entertainment: '¥500 – ¥1,200/month',
      total: '¥3,400 – ¥7,700/month'
    },
    scholarships: [
    {
      name: 'Chinese Government Scholarship (CSC)',
      amount: 'Full tuition + ¥2,500 – ¥3,500/month stipend + accommodation',
      eligibility: 'International students with strong academics (50,000+ awards/year)'
    },
    {
      name: 'Confucius Institute Scholarship',
      amount: 'Full or partial tuition + ¥1,400 – ¥2,500/month',
      eligibility: 'Students studying Chinese language and culture'
    },
    {
      name: 'University Scholarships',
      amount: '30–100% tuition waiver',
      eligibility: 'Merit-based for international students'
    }],

    careerOpportunities: [
    'Rapidly growing technology and innovation sectors (Alibaba, Tencent, Huawei)',
    'Opportunities in manufacturing and global trade',
    'Teaching English and other languages',
    'International business and finance roles',
    'Growing startup ecosystem (Beijing, Shanghai, Shenzhen)',
    'Gateway to Asian markets'],

    culturalInsights: [
    '5,000 years of rich cultural heritage',
    'Learning Mandarin opens vast opportunities globally',
    'Respect for hierarchy and tradition',
    'Modern cities with ancient historical sites',
    'Diverse regional cuisines and customs',
    'Rapid technological advancement (WeChat, Alipay)'],

    testimonials: [
    {
      name: 'Sarah Johnson',
      university: 'Tsinghua University',
      quote: "China's scholarship program made my dream affordable. The experience of living in Beijing while studying at a top university has been incredible.",
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1986eac6c-1772112040668.png',
      imageAlt: 'Portrait of smiling female student'
    },
    {
      name: 'Mohammed Ali',
      university: 'Peking University',
      quote: 'The combination of ancient culture and modern innovation in China is fascinating. The education quality and opportunities are world-class.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_143b2ab7a-1765392983469.png',
      imageAlt: 'Portrait of male student with laptop'
    }],

    faqs: [
    {
      question: 'Do I need to speak Mandarin to study in China?',
      answer: "Many universities offer programs in English, especially at Master's level. However, learning Mandarin is highly beneficial for daily life and career opportunities in China."
    },
    {
      question: 'Are Chinese degrees recognized internationally?',
      answer: 'Yes, degrees from top Chinese universities (Tsinghua, PKU, Fudan) are increasingly recognized worldwide. Many Chinese universities rank among the world\'s best in various fields.'
    },
    {
      question: 'How easy is it to get a scholarship?',
      answer: 'China offers numerous scholarship opportunities. The CSC scholarship provides 50,000+ awards annually. University scholarships are also widely available based on academic merit.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'International students can work on-campus with university approval. Off-campus work requires additional permits from the local Public Security Bureau.'
    },
    {
      question: 'What is the cost of living in China?',
      answer: 'China is quite affordable for students. Living costs vary by city: Beijing and Shanghai are more expensive (¥4,000–¥7,000/month), while smaller cities offer very affordable living (¥2,500–¥4,000/month).'
    }]

  },
  japan: {
    id: 'japan',
    name: 'Japan',
    flag: '🇯🇵',
    description: 'Study in the land of technological innovation and cultural tradition. Japan offers world-leading research, unique cultural experiences, and excellent career opportunities in a safe, efficient society with over 300,000 international students.',
    capital: 'Tokyo',
    language: 'Japanese, English (in some programs)',
    currency: 'Japanese Yen',
    currencySymbol: '¥',
    population: '125 million',
    timezone: 'JST (UTC+9)',
    climate: 'Four distinct seasons, temperate',
    bestTimeToVisit: 'March to May (cherry blossoms), September to November',
    heroImage: 'https://images.unsplash.com/photo-1681317474435-91e0f5065abf',
    heroImageAlt: 'Mount Fuji with cherry blossoms in foreground and traditional Japanese pagoda',
    highlights: [
    'University of Tokyo ranked #28 globally (QS 2025)',
    'Unique blend of tradition and modernity',
    'MEXT Scholarship: Full tuition + ¥117,000 – ¥145,000/month',
    'Safe and efficient living environment',
    'Rich cultural experiences – 4 seasons',
    'Strong job market for graduates'],

    universities: [
    {
      name: 'University of Tokyo (UTokyo)',
      ranking: '#28 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Sciences', 'Medicine', 'Humanities'],
      tuitionRange: '¥535,800/year (national university)',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1b39678d6-1770356921588.png',
      imageAlt: 'Traditional Japanese university campus with modern facilities'
    },
    {
      name: 'Kyoto University',
      ranking: '#46 Global – QS World Rankings 2025',
      programs: ['Sciences', 'Engineering', 'Medicine', 'Agriculture'],
      tuitionRange: '¥535,800/year (national university)',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1aa1da4f5-1774854238226.png',
      imageAlt: 'Historic university building in Kyoto'
    },
    {
      name: 'Waseda University',
      ranking: 'Top Private University – QS Japan 2025',
      programs: ['Business', 'International Relations', 'Engineering', 'Sciences'],
      tuitionRange: '¥1,100,000 – ¥1,600,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_10eb3cbe5-1774656344586.png',
      imageAlt: 'Modern university campus in Tokyo'
    }],

    visaRequirements: [
    {
      type: 'Student Visa (Ryugaku – 留学)',
      processingTime: '3–5 weeks',
      requirements: [
      'Valid passport',
      'Certificate of Eligibility (COE – 在留資格認定証明書) from university',
      'Admission letter from Japanese institution',
      'Proof of financial support (¥2,000,000+ or scholarship letter)',
      'Health insurance coverage',
      'Passport-sized photographs',
      'Completed visa application form',
      'Residence card (issued on arrival)']

    }],

    costOfLiving: {
      accommodation: '¥40,000 – ¥80,000/month (dormitory ¥15,000 – ¥30,000)',
      food: '¥30,000 – ¥50,000/month',
      transportation: '¥10,000 – ¥15,000/month (student discount available)',
      utilities: '¥10,000 – ¥15,000/month',
      entertainment: '¥20,000 – ¥30,000/month',
      total: '¥110,000 – ¥190,000/month'
    },
    scholarships: [
    {
      name: 'MEXT Scholarship (Japanese Government)',
      amount: 'Full tuition + ¥117,000 – ¥145,000/month stipend',
      eligibility: 'Outstanding academic merit (highly competitive)'
    },
    {
      name: 'JASSO Scholarships',
      amount: '¥48,000 – ¥80,000/month',
      eligibility: 'International students with good academics'
    },
    {
      name: 'University Scholarships',
      amount: 'Partial to full tuition waiver',
      eligibility: 'Merit-based'
    }],

    careerOpportunities: [
    'Leading technology and robotics companies (Sony, Toyota, Honda)',
    'Automotive and manufacturing industries',
    'Growing startup ecosystem (Tokyo, Osaka)',
    'Teaching English (JET Programme)',
    'International business roles',
    'Research and development positions'],

    culturalInsights: [
    'Respect and politeness (礼儀) highly valued',
    'Punctuality is essential – be on time',
    'Unique blend of ancient and modern culture',
    'Four beautiful seasons with festivals (Hanami, Obon)',
    'Excellent public transportation (Shinkansen)',
    'Safe society with very low crime rates'],

    testimonials: [
    {
      name: 'Emily Chen',
      university: 'University of Tokyo',
      quote: 'Japan offers an incredible combination of cutting-edge technology and rich cultural traditions. The research opportunities are world-class.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c440ad12-1765286115178.png',
      imageAlt: 'Portrait of smiling female student'
    },
    {
      name: 'David Kim',
      university: 'Kyoto University',
      quote: 'Studying in Japan has been transformative. The quality of education and the cultural experience are beyond my expectations.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_160126dd8-1773178814596.png',
      imageAlt: 'Portrait of confident male student'
    }],

    faqs: [
    {
      question: "Do I need to speak Japanese to study in Japan?",
      answer: "Many Master's programs are offered in English. However, learning Japanese (JLPT N2+) is highly recommended for daily life and significantly improves job prospects and cultural integration."
    },
    {
      question: 'How expensive is Japan for students?',
      answer: 'National university tuition is ¥535,800/year ($3,600). Living costs are ¥110,000–¥190,000/month. Many scholarships are available, and part-time work (up to 28 hours/week) helps offset expenses.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'Yes, international students can work up to 28 hours per week during term time and 40 hours per week during holidays (June–September and December–January) with a work permit.'
    },
    {
      question: 'What are the job prospects after graduation?',
      answer: 'Japan actively seeks international talent. Graduates can apply for a Designated Activities Visa (job-hunting) for up to 1 year. Many find employment in technology, engineering, and international business sectors.'
    },
    {
      question: 'Is Japan safe for international students?',
      answer: 'Japan is consistently ranked among the top 5 safest countries in the world with very low crime rates. The society is orderly, and public transportation is extremely reliable.'
    }]

  },
  vietnam: {
    id: 'vietnam',
    name: 'Vietnam',
    flag: '🇻🇳',
    description: "Discover affordable quality education in Southeast Asia's emerging hub. Vietnam offers low living costs, growing economy, beautiful landscapes, and a welcoming culture for international students. Vietnam's GDP growth of 6–7% annually creates excellent opportunities.",
    capital: 'Hanoi',
    language: 'Vietnamese, English (in many programs)',
    currency: 'Vietnamese Dong',
    currencySymbol: '₫',
    population: '98 million',
    timezone: 'ICT (UTC+7)',
    climate: 'Tropical climate, warm year-round',
    bestTimeToVisit: 'November to April',
    heroImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d6c6b838-1772253351479.png',
    heroImageAlt: 'Ha Long Bay in Vietnam with limestone karsts and emerald waters with traditional boats',
    highlights: [
    'Very affordable tuition: ₫20,000,000 – ₫80,000,000/year',
    'Growing economy with 6–7% annual GDP growth',
    'Beautiful natural landscapes (Ha Long Bay, Hoi An)',
    'Friendly and welcoming culture',
    'Increasing number of English programs',
    'Strategic location in Southeast Asia'],

    universities: [
    {
      name: 'Vietnam National University, Hanoi (VNU-HN)',
      ranking: 'Top University in Vietnam – QS Asia 2025',
      programs: ['Business', 'Engineering', 'Sciences', 'International Studies'],
      tuitionRange: '₫20,000,000 – ₫50,000,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17c374ec0-1766771639577.png',
      imageAlt: 'Modern university campus in Hanoi'
    },
    {
      name: 'RMIT University Vietnam',
      ranking: 'Australian Degree – Internationally Recognised',
      programs: ['Business', 'Design', 'Engineering', 'Communication'],
      tuitionRange: '₫200,000,000 – ₫380,000,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17c374ec0-1766771639577.png',
      imageAlt: 'International university campus with modern facilities'
    },
    {
      name: 'University of Economics Ho Chi Minh City (UEH)',
      ranking: 'Leading Business School in Vietnam',
      programs: ['Economics', 'Business', 'Finance', 'Management'],
      tuitionRange: '₫25,000,000 – ₫55,000,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c0da8502-1767527082761.png',
      imageAlt: 'Business school building in Ho Chi Minh City'
    }],

    visaRequirements: [
    {
      type: 'Student Visa (DH – Du Học)',
      processingTime: '2–3 weeks',
      requirements: [
      'Valid passport (minimum 6 months validity)',
      'Acceptance letter from Vietnamese university',
      'Visa approval letter (obtained through university)',
      'Health certificate',
      'Police clearance certificate',
      'Passport-sized photographs',
      'Proof of financial support',
      'Completed visa application form']

    }],

    costOfLiving: {
      accommodation: '₫3,000,000 – ₫7,000,000/month',
      food: '₫2,000,000 – ₫4,000,000/month',
      transportation: '₫500,000 – ₫1,200,000/month',
      utilities: '₫500,000 – ₫1,200,000/month',
      entertainment: '₫1,000,000 – ₫2,500,000/month',
      total: '₫7,000,000 – ₫15,900,000/month'
    },
    scholarships: [
    {
      name: 'Vietnamese Government Scholarship (VIED)',
      amount: 'Full or partial tuition + ₫3,000,000 – ₫5,000,000/month',
      eligibility: 'International students with good academics'
    },
    {
      name: 'University Merit Scholarships',
      amount: '20–50% tuition waiver',
      eligibility: 'High academic achievement'
    },
    {
      name: 'ASEAN Scholarships',
      amount: 'Varies by program',
      eligibility: 'Students from ASEAN countries'
    }],

    careerOpportunities: [
    'Growing technology and IT sectors (Samsung, Intel, LG manufacturing hubs)',
    'Manufacturing and export industries',
    'Teaching English opportunities (high demand)',
    'Tourism and hospitality sector',
    'International business roles',
    'Emerging startup ecosystem (Ho Chi Minh City)'],

    culturalInsights: [
    'Friendly and hospitable people',
    'Rich history and cultural heritage (1,000+ years)',
    'Delicious and diverse cuisine (Pho, Banh Mi)',
    'Beautiful natural landscapes from north to south',
    'Growing expat community',
    'Affordable lifestyle'],

    testimonials: [
    {
      name: 'Lisa Nguyen',
      university: 'RMIT University Vietnam',
      quote: 'Vietnam offers an amazing combination of quality education and affordable living. The culture is vibrant and the people are incredibly welcoming.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1986eac6c-1772112040668.png',
      imageAlt: 'Portrait of female student in university setting'
    },
    {
      name: 'James Wilson',
      university: 'Vietnam National University',
      quote: 'Studying in Vietnam has been an adventure. The low cost of living allowed me to focus on my studies while exploring this beautiful country.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_143b2ab7a-1765392983469.png',
      imageAlt: 'Portrait of male student with laptop'
    }],

    faqs: [
    {
      question: 'Do I need to speak Vietnamese to study in Vietnam?',
      answer: 'Many universities offer programs in English, especially international universities and business programs. Learning basic Vietnamese enhances your daily life experience.'
    },
    {
      question: 'How affordable is Vietnam for students?',
      answer: 'Vietnam is one of the most affordable study destinations in Asia. Total monthly living costs are ₫7,000,000–₫16,000,000 ($280–$640). Local university tuition is ₫20,000,000–₫50,000,000/year ($800–$2,000).'
    },
    {
      question: 'Is Vietnam safe for international students?',
      answer: 'Yes, Vietnam is generally safe for international students. The crime rate is relatively low, and Vietnamese people are known for their hospitality toward foreigners.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'Work opportunities for international students are limited. Teaching English part-time is a common option (₫200,000–₫500,000/hour). Check visa regulations for specific work permissions.'
    },
    {
      question: 'What is the quality of education in Vietnam?',
      answer: "Vietnam's education system is improving rapidly. International branch campuses (RMIT, BUV) and partnerships with foreign universities offer quality education with international standards."
    }]

  },
  philippines: {
    id: 'philippines',
    name: 'Philippines',
    flag: '🇵🇭',
    description: 'Study in an English-speaking tropical paradise with American-style education. The Philippines offers affordable tuition, beautiful islands, warm climate, and a friendly culture. Home to 2,300+ higher education institutions.',
    capital: 'Manila',
    language: 'English, Filipino',
    currency: 'Philippine Peso',
    currencySymbol: '₱',
    population: '115 million',
    timezone: 'PHT (UTC+8)',
    climate: 'Tropical climate, warm year-round',
    bestTimeToVisit: 'November to April',
    heroImage: 'https://images.unsplash.com/photo-1621692359226-070bd08a954d',
    heroImageAlt: 'Tropical beach in Philippines with white sand, turquoise water, and palm trees',
    highlights: [
    'Fully English-medium instruction (2nd largest English-speaking country in Asia)',
    'American-style education system (K–12 + 4-year college)',
    'Very low cost of living and tuition',
    'Beautiful islands and warm tropical climate',
    'Friendly and hospitable culture',
    'WHO-recognized medical degrees'],

    universities: [
    {
      name: 'University of the Philippines (UP)',
      ranking: '#1 in Philippines – QS Asia 2025',
      programs: ['Medicine', 'Engineering', 'Business', 'Sciences'],
      tuitionRange: '₱30,000 – ₱120,000/year',
      image: 'https://images.unsplash.com/photo-1504682041639-04e526da82b9',
      imageAlt: 'University campus with tropical vegetation'
    },
    {
      name: 'Ateneo de Manila University',
      ranking: 'Top 5 Private University in Philippines',
      programs: ['Business', 'Law', 'Liberal Arts', 'Sciences'],
      tuitionRange: '₱90,000 – ₱180,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_16b8ecaa9-1768045404124.png',
      imageAlt: 'Private university campus with modern facilities'
    },
    {
      name: 'De La Salle University (DLSU)',
      ranking: 'Top 5 Research University in Philippines',
      programs: ['Engineering', 'Business', 'Education', 'Sciences'],
      tuitionRange: '₱80,000 – ₱160,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_12f4fff37-1770791815456.png',
      imageAlt: 'University building with students walking'
    }],

    visaRequirements: [
    {
      type: 'Student Visa (9F) – Bureau of Immigration Philippines',
      processingTime: '2–4 weeks',
      requirements: [
      'Valid passport (minimum 6 months validity)',
      'Acceptance letter from CHED-recognized Philippine university',
      'Proof of financial capacity (₱50,000+ or bank statement)',
      'Medical certificate (including chest X-ray)',
      'Police clearance certificate',
      'Birth certificate (authenticated)',
      'Passport-sized photographs',
      'Completed visa application form']

    }],

    costOfLiving: {
      accommodation: '₱5,000 – ₱18,000/month',
      food: '₱5,000 – ₱12,000/month',
      transportation: '₱2,000 – ₱5,000/month',
      utilities: '₱2,000 – ₱5,000/month',
      entertainment: '₱3,000 – ₱7,000/month',
      total: '₱17,000 – ₱47,000/month'
    },
    scholarships: [
    {
      name: 'Philippine Government Scholarship (CHED)',
      amount: 'Full tuition + ₱5,000 – ₱10,000/month allowance',
      eligibility: 'International students from partner countries'
    },
    {
      name: 'University Merit Scholarships',
      amount: '25–100% tuition waiver',
      eligibility: 'High academic achievement'
    },
    {
      name: 'ASEAN Scholarships',
      amount: 'Partial tuition',
      eligibility: 'Students from ASEAN countries'
    }],

    careerOpportunities: [
    'Growing BPO and IT-BPM industries ($32 billion sector)',
    'Teaching opportunities (high demand for English teachers)',
    'Tourism and hospitality sector',
    'Healthcare and medical fields',
    'International business roles',
    'Emerging startup ecosystem (BGC, Makati)'],

    culturalInsights: [
    'Warm and hospitable people (Bayanihan spirit)',
    'English widely spoken everywhere',
    'Blend of Asian and Western cultures',
    'Beautiful beaches and natural attractions (7,641 islands)',
    'Vibrant festivals and celebrations (Sinulog, Ati-Atihan)',
    'Family-oriented society'],

    testimonials: [
    {
      name: 'Yuki Tanaka',
      university: 'University of the Philippines',
      quote: 'The Philippines surprised me with its quality education and incredibly affordable costs. The English-speaking environment made adaptation easy.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c440ad12-1765286115178.png',
      imageAlt: 'Portrait of smiling female student'
    },
    {
      name: 'Omar Abdullah',
      university: 'Ateneo de Manila University',
      quote: 'Studying in the Philippines has been wonderful. The people are friendly, the education is solid, and the tropical lifestyle is amazing.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_160126dd8-1773178814596.png',
      imageAlt: 'Portrait of confident male student'
    }],

    faqs: [
    {
      question: 'Is English really used for instruction?',
      answer: 'Yes, English is one of the official languages and is used as the medium of instruction in all universities. The Philippines has the 3rd highest English proficiency in Asia.'
    },
    {
      question: 'How affordable is the Philippines?',
      answer: 'The Philippines is extremely affordable. Total monthly living costs are ₱17,000–₱47,000 ($300–$840). Tuition fees are among the lowest in Asia.'
    },
    {
      question: 'Is the education quality good?',
      answer: 'Yes, the Philippines follows an American-style education system. Top universities offer quality education, and medical degrees (MD) are recognized by WHO and accepted for USMLE/PLAB.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'International students have limited work opportunities. However, teaching English or tutoring is possible with proper permits from the Bureau of Immigration.'
    },
    {
      question: 'What about safety and weather?',
      answer: 'Major cities and university areas are generally safe. The tropical climate is warm year-round with a rainy season from June to November. Typhoons can occur but universities have safety protocols.'
    }]

  },
  'saudi-arabia': {
    id: 'saudi-arabia',
    name: 'Saudi Arabia',
    flag: '🇸🇦',
    description: 'Experience modern education with generous scholarships in the heart of the Middle East. Saudi Arabia offers fully-funded programs, state-of-the-art facilities, and unique cultural experiences under Vision 2030.',
    capital: 'Riyadh',
    language: 'Arabic, English (in many programs)',
    currency: 'Saudi Riyal',
    currencySymbol: 'SAR',
    population: '36 million',
    timezone: 'AST (UTC+3)',
    climate: 'Desert climate, hot and dry',
    bestTimeToVisit: 'November to February',
    heroImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_18e777df0-1772583417028.png',
    heroImageAlt: 'Modern Riyadh skyline with Kingdom Centre Tower and contemporary architecture',
    highlights: [
    'Fully-funded scholarship programs (SACM)',
    'Modern universities with cutting-edge facilities',
    'Tax-free income and benefits',
    'Vision 2030 – massive investment in education',
    'Growing economy and job market',
    'Rich cultural and religious heritage'],

    universities: [
    {
      name: 'King Fahd University of Petroleum & Minerals (KFUPM)',
      ranking: '#186 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Sciences', 'Business', 'Computer Science'],
      tuitionRange: 'Free (with scholarship)',
      image: 'https://images.unsplash.com/photo-1610285632237-9161978d9ea3',
      imageAlt: 'Modern university campus with Islamic architectural elements'
    },
    {
      name: 'King Saud University (KSU)',
      ranking: '#221 Global – QS World Rankings 2025',
      programs: ['Medicine', 'Engineering', 'Sciences', 'Humanities'],
      tuitionRange: 'Free (with scholarship)',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1e1f58b7d-1767885611668.png',
      imageAlt: 'University campus with state-of-the-art facilities'
    },
    {
      name: 'King Abdullah University of Science and Technology (KAUST)',
      ranking: '#166 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Sciences', 'Environmental Studies', 'AI'],
      tuitionRange: 'Free (fully funded)',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1182615ef-1774854237437.png',
      imageAlt: 'Research university campus by the Red Sea'
    }],

    visaRequirements: [
    {
      type: 'Saudi Student Visa',
      processingTime: '4–6 weeks',
      requirements: [
      'Valid passport (minimum 6 months validity)',
      'Scholarship letter from Saudi university/SACM',
      'Medical fitness certificate',
      'Police clearance certificate',
      'Attested educational certificates',
      'Passport-sized photographs',
      'Completed visa application form',
      'Health insurance']

    }],

    costOfLiving: {
      accommodation: 'Free (provided by scholarship)',
      food: 'SAR 800 – SAR 1,800/month',
      transportation: 'SAR 200 – SAR 500/month',
      utilities: 'Included in accommodation',
      entertainment: 'SAR 500 – SAR 1,200/month',
      total: 'SAR 1,500 – SAR 3,500/month'
    },
    scholarships: [
    {
      name: 'Saudi Arabian Cultural Mission (SACM) Scholarship',
      amount: 'Full tuition + stipend + accommodation + flights',
      eligibility: 'International students with strong academics'
    },
    {
      name: 'KAUST Fellowship',
      amount: 'Full funding + SAR 2,000 – SAR 4,000/month stipend',
      eligibility: 'Outstanding graduate students in STEM'
    },
    {
      name: 'University Scholarships',
      amount: 'Full tuition + benefits',
      eligibility: 'Merit-based for international students'
    }],

    careerOpportunities: [
    'Growing technology and innovation sectors (NEOM, Vision 2030)',
    'Opportunities in oil and gas industry (Saudi Aramco)',
    'Vision 2030 development projects',
    'Healthcare and education sectors',
    'Tax-free income for professionals',
    'Gateway to Middle East markets'],

    culturalInsights: [
    'Islamic culture and traditions',
    'Respect for local customs and dress code',
    'Generous hospitality',
    'Modern cities with traditional values',
    'Growing entertainment and tourism sector (Vision 2030)',
    'Strategic location for travel in Middle East'],

    testimonials: [
    {
      name: 'Fatima Ahmed',
      university: 'King Saud University',
      quote: 'The scholarship program covered everything - tuition, accommodation, and even a monthly stipend. The facilities are world-class.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1986eac6c-1772112040668.png',
      imageAlt: 'Portrait of female student in university setting'
    },
    {
      name: 'Ali Hassan',
      university: 'KFUPM',
      quote: 'Studying in Saudi Arabia has been an incredible opportunity. The education quality and research facilities rival the best in the world.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_14ff30f05-1763294027584.png',
      imageAlt: 'Portrait of male engineering student with laptop'
    }],

    faqs: [
    {
      question: 'Are scholarships really fully funded?',
      answer: "Yes, Saudi government scholarships (SACM) typically cover full tuition, accommodation, monthly stipend (SAR 850–SAR 2,000), health insurance, and round-trip flights. It's one of the most generous scholarship programs globally."
    },
    {
      question: 'Do I need to speak Arabic?',
      answer: 'Many programs, especially at graduate level, are offered in English. However, learning Arabic is beneficial for daily life and cultural integration.'
    },
    {
      question: 'What about cultural restrictions?',
      answer: 'Saudi Arabia has specific cultural norms and dress codes that must be respected. The country is modernizing under Vision 2030, with new entertainment venues, cinemas, and mixed-gender events.'
    },
    {
      question: 'Can I work while studying?',
      answer: 'Work opportunities for international students are limited. Most scholarship programs provide sufficient stipends, so working is generally not necessary.'
    },
    {
      question: 'Is it safe for international students?',
      answer: 'Yes, Saudi Arabia is very safe with low crime rates. Universities provide secure campuses and comprehensive support services for international students.'
    }]

  },
  'south-korea': {
    id: 'south-korea',
    name: 'South Korea',
    flag: '🇰🇷',
    description: "Study in Asia's technology powerhouse with vibrant K-culture. South Korea offers world-class education, cutting-edge technology, generous scholarships, and unique cultural experiences. Home to Samsung, LG, and Hyundai.",
    capital: 'Seoul',
    language: 'Korean, English (in many programs)',
    currency: 'South Korean Won',
    currencySymbol: '₩',
    population: '52 million',
    timezone: 'KST (UTC+9)',
    climate: 'Four distinct seasons, temperate',
    bestTimeToVisit: 'March to May, September to November',
    heroImage: 'https://images.unsplash.com/photo-1678888703077-0813878c6d78',
    heroImageAlt: 'Seoul cityscape with Namsan Tower, modern skyscrapers, and traditional architecture blend',
    highlights: [
    'Seoul National University ranked #31 globally (QS 2025)',
    'Vibrant K-pop, K-drama, and entertainment culture',
    'KGSP Scholarship: Full tuition + ₩900,000/month',
    'High-speed internet and modern infrastructure',
    'Safe and efficient society',
    'Blend of tradition and modernity'],

    universities: [
    {
      name: 'Seoul National University (SNU)',
      ranking: '#31 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Business', 'Medicine', 'Sciences'],
      tuitionRange: '₩4,000,000 – ₩6,500,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1ded083d0-1774854241120.png',
      imageAlt: 'Prestigious university campus in Seoul'
    },
    {
      name: 'KAIST (Korea Advanced Institute of Science and Technology)',
      ranking: '#47 Global – QS World Rankings 2025',
      programs: ['Engineering', 'Computer Science', 'Natural Sciences', 'Business'],
      tuitionRange: '₩5,000,000 – ₩7,500,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_11b2b572b-1770220191856.png',
      imageAlt: 'Modern technology university campus'
    },
    {
      name: 'Yonsei University',
      ranking: '#56 Global – QS World Rankings 2025',
      programs: ['Business', 'Medicine', 'Engineering', 'International Studies'],
      tuitionRange: '₩6,500,000 – ₩10,000,000/year',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_19a121da7-1764641104290.png',
      imageAlt: 'University students in modern learning environment'
    }],

    visaRequirements: [
    {
      type: 'D-2 Student Visa (유학)',
      processingTime: '3–5 weeks',
      requirements: [
      'Valid passport',
      'Certificate of admission from Korean university',
      'Proof of financial ability (USD 10,000+ or ₩13,000,000+)',
      'Academic transcripts',
      'Study plan',
      'Health certificate',
      'Passport-sized photographs',
      'Completed visa application form']

    }],

    costOfLiving: {
      accommodation: '₩300,000 – ₩800,000/month (dormitory ₩200,000 – ₩400,000)',
      food: '₩300,000 – ₩600,000/month',
      transportation: '₩60,000 – ₩100,000/month (T-money card)',
      utilities: '₩100,000 – ₩200,000/month',
      entertainment: '₩200,000 – ₩500,000/month',
      total: '₩960,000 – ₩2,200,000/month'
    },
    scholarships: [
    {
      name: 'Korean Government Scholarship (KGSP)',
      amount: 'Full tuition + ₩900,000/month stipend + Korean language training',
      eligibility: 'Outstanding international students (highly competitive)'
    },
    {
      name: 'University Scholarships',
      amount: '30–100% tuition waiver',
      eligibility: 'Academic merit and language proficiency'
    },
    {
      name: 'Global Korea Scholarship (GKS)',
      amount: 'Full tuition + ₩900,000 – ₩1,000,000/month',
      eligibility: 'Students in specific fields'
    }],

    careerOpportunities: [
    'Leading technology companies (Samsung, LG, SK Hynix, Hyundai)',
    'Growing startup and gaming industries (Krafton, Kakao)',
    'Entertainment and media sector (K-content)',
    'Teaching English opportunities (EPIK Programme)',
    'International business roles',
    'Research and development positions'],

    culturalInsights: [
    'Vibrant K-pop and K-drama culture',
    'Respect for hierarchy and elders (Confucian values)',
    'Fast-paced modern lifestyle',
    'Delicious and diverse cuisine (Kimchi, Bibimbap)',
    'Four beautiful seasons',
    'Blend of Buddhist and Confucian traditions'],

    testimonials: [
    {
      name: 'Emma Rodriguez',
      university: 'Seoul National University',
      quote: 'South Korea exceeded all my expectations. The technology, culture, and education quality are world-class. The KGSP scholarship made it possible.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1c440ad12-1765286115178.png',
      imageAlt: 'Portrait of smiling female student'
    },
    {
      name: 'Michael Park',
      university: 'KAIST',
      quote: 'The combination of ancient culture and modern innovation in South Korea is fascinating. The education quality and opportunities are world-class.',
      image: 'https://img.rocket.new/generatedImages/rocket_gen_img_13a9a524b-1763296756971.png',
      imageAlt: 'Portrait of confident male student'
    }],

    faqs: [
    {
      question: 'Do I need to speak Korean to study in South Korea?',
      answer: "Many universities offer programs in English, especially at Master's level. However, learning Korean (TOPIK Level 3+) is highly beneficial for daily life and significantly improves your experience and job prospects."
    },
    {
      question: 'How competitive are Korean scholarships?',
      answer: 'The KGSP is competitive but offers generous benefits. Many universities also offer their own scholarships based on academic merit. Apply early (September–October for the following year).'
    },
    {
      question: 'Can I work while studying?',
      answer: 'Yes, D-2 visa holders can work up to 20 hours per week during semester and full-time during vacations with proper permission from the immigration office.'
    },
    {
      question: 'What is the cost of living in South Korea?',
      answer: 'Seoul is more expensive, but overall costs are moderate. Total monthly costs are ₩960,000–₩2,200,000 ($720–$1,650). Student discounts are widely available.'
    },
    {
      question: 'Are there job opportunities after graduation?',
      answer: 'Yes, South Korea actively seeks international talent, especially in technology and engineering. The D-10 job-seeking visa allows graduates to stay for up to 6 months to find employment.'
    }]

  }
};

// Ireland data added as a separate entry
const irelandData: Country = {
  id: 'ireland',
  name: 'Ireland',
  flag: '🇮🇪',
  description: 'Study in the Emerald Isle — a thriving English-speaking hub for technology, pharmaceuticals, and finance. Ireland combines world-class universities, a welcoming culture, and a gateway to the European Union. Home to Google, Apple, Meta, and Pfizer European HQs.',
  capital: 'Dublin',
  language: 'English, Irish (Gaeilge)',
  currency: 'Euro',
  currencySymbol: '€',
  population: '5.1 million',
  timezone: 'GMT (UTC+0) / IST (UTC+1 in summer)',
  climate: 'Temperate oceanic, mild and rainy',
  bestTimeToVisit: 'May to September',
  heroImage: 'https://img.rocket.new/generatedImages/rocket_gen_img_1be5585fc-1772113931761.png',
  heroImageAlt: 'Cliffs of Moher in Ireland with dramatic Atlantic Ocean views and green coastal landscape',
  highlights: [
  'English-speaking EU country',
  'Home to Google, Apple, Meta, LinkedIn, and Pfizer European HQs',
  'Post-study stay-back visa: Stamp 1G (up to 2 years)',
  'World-class research universities',
  'Vibrant student cities — Dublin, Cork, Galway',
  'Gateway to European job market'],

  universities: [
  {
    name: 'Trinity College Dublin (TCD)',
    ranking: '#81 Global – QS World Rankings 2025',
    programs: ['Business', 'Computer Science', 'Law', 'Medicine'],
    tuitionRange: '€16,000 – €25,000/year',
    image: 'https://images.unsplash.com/photo-1722942848983-9bc478fee2b0',
    imageAlt: 'Trinity College Dublin historic campus with cobblestone courtyard'
  },
  {
    name: 'University College Dublin (UCD)',
    ranking: '#181 Global – QS World Rankings 2025',
    programs: ['Engineering', 'Business', 'Sciences', 'Agriculture'],
    tuitionRange: '€14,000 – €22,000/year',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_17c374ec0-1766771639577.png',
    imageAlt: 'Modern university campus with students walking between buildings'
  },
  {
    name: 'University College Cork (UCC)',
    ranking: '#303 Global – QS World Rankings 2025',
    programs: ['Medicine', 'Law', 'Arts', 'Science'],
    tuitionRange: '€12,000 – €20,000/year',
    image: 'https://images.unsplash.com/photo-1596585357154-14df253c17d4',
    imageAlt: 'University College Cork historic stone buildings surrounded by green trees'
  }],

  visaRequirements: [
  {
    type: 'Irish Student Visa (Study Visa / Stamp 2)',
    processingTime: '4–8 weeks',
    requirements: [
    'Valid passport (minimum 12 months validity)',
    'Letter of acceptance from an Irish institution (ILEP-registered)',
    'Proof of funds: €7,000+ for first year (€3,000 for renewals)',
    'Private medical insurance',
    'Completed online visa application (AVATS system)',
    'Passport-sized photographs',
    'Evidence of English language proficiency (IELTS 6.0+ / TOEFL 79+)',
    'Proof of accommodation in Ireland']

  }],

  costOfLiving: {
    accommodation: '€700 – €1,400/month (Dublin higher; Cork/Galway lower)',
    food: '€250 – €450/month',
    transportation: '€100 – €150/month (Leap Card)',
    utilities: '€80 – €130/month',
    entertainment: '€150 – €300/month',
    total: '€1,280 – €2,430/month'
  },
  scholarships: [
  {
    name: 'Government of Ireland International Education Scholarship',
    amount: '€10,000 (one-time award)',
    eligibility: 'Non-EU/EEA students with outstanding academic record'
  },
  {
    name: 'University Merit Scholarships',
    amount: '€3,000 – €7,500/year',
    eligibility: 'High academic achievement (GPA 3.5+ or equivalent)'
  },
  {
    name: 'STEM Excellence Award',
    amount: 'Up to €7,500',
    eligibility: 'Students enrolled in STEM programs'
  }],

  careerOpportunities: [
  'Post-study Stamp 1G visa for up to 2 years',
  'Headquarters of Google, Apple, Meta, LinkedIn, Pfizer',
  'Thriving pharmaceutical and biotech sectors',
  'Growing fintech and financial services industry',
  'Access to EU job market with Irish degree',
  'Strong demand for tech and engineering graduates'],

  culturalInsights: [
  'Warm and welcoming Irish hospitality',
  'Rich literary and musical heritage (Joyce, Yeats, U2)',
  'Pub culture is central to social life',
  'Stunning natural landscapes — Cliffs of Moher, Ring of Kerry',
  'Safe country with low crime rates',
  'Diverse and multicultural student communities'],

  testimonials: [
  {
    name: 'Ananya Krishnan',
    university: 'Trinity College Dublin',
    quote: 'Ireland was the perfect choice — English-speaking, EU access, and incredible career opportunities in tech. Dublin feels like home now.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1986eac6c-1772112040668.png',
    imageAlt: 'Portrait of smiling female student in university setting'
  },
  {
    name: 'Rahul Mehta',
    university: 'University College Dublin',
    quote: 'Studying in Ireland opened doors to the European tech industry. The education quality and career support are exceptional.',
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_160126dd8-1773178814596.png',
    imageAlt: 'Portrait of confident male student with laptop'
  }],

  faqs: [
  {
    question: 'Can I work while studying in Ireland?',
    answer: 'Yes, students on a Stamp 2 visa can work up to 20 hours per week during term time and 40 hours per week during holidays (June–September and December–January).'
  },
  {
    question: 'What is the post-study work visa in Ireland?',
    answer: 'Ireland offers a Third Level Graduate Programme (Stamp 1G): 12 months for NFQ Level 7/8 graduates, 24 months for NFQ Level 9/10 (Master\'s/PhD) graduates.'
  },
  {
    question: 'Is Ireland expensive compared to other EU countries?',
    answer: 'Dublin is one of the more expensive cities in Europe, but other cities like Cork, Galway, and Limerick offer significantly more affordable living costs (20–30% lower). Scholarships and part-time work help offset expenses.'
  },
  {
    question: 'Are Irish degrees recognized internationally?',
    answer: "Yes, Irish degrees are globally recognized and highly respected, especially in the EU, UK, USA, Canada, and Australia. Ireland's education system is ranked among the top globally."
  },
  {
    question: 'What English language tests are accepted?',
    answer: 'Most Irish universities accept IELTS (6.0–6.5+), TOEFL iBT (79–90+), PTE Academic (55–65+), and some also accept Duolingo English Test (110+).'
  }]

};

// Merge Ireland into countryData lookup
const allCountryData: Record<string, Country> = {
  ...countryData,
  ireland: irelandData
};

export default function CountryDetailPage() {
  const params = useParams();
  const countryId = params?.id as string;
  const country = allCountryData[countryId];

  const [activeTab, setActiveTab] = useState<'universities' | 'visa' | 'costs' | 'scholarships'>('universities');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  if (!country) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-foreground mb-4">Country Not Found</h1>
          <p className="text-muted-foreground mb-8">The destination you're looking for doesn't exist.</p>
          <Link
            href="/destinations"
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors">
            
            Explore All Destinations
          </Link>
        </div>
      </div>);

  }

  const breadcrumbSchema: WithContext<BreadcrumbList> = generateBreadcrumbSchema([
  { name: 'Home', path: '/' },
  { name: 'Study Destinations', path: '/destinations' },
  { name: country.name, path: `/destinations/${countryId}` }]
  );

  return (
    <div className="min-h-screen bg-background">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      <Header />
      {/* Hero Section */}
      <div className="relative h-[500px] overflow-hidden">
        <Image
          src={country.heroImage}
          alt={country.heroImageAlt}
          fill
          className="object-cover"
          priority />
        
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl px-4">
            <div className="text-6xl mb-4">{country.flag}</div>
            <h1 className="text-5xl md:text-6xl font-bold mb-4">{country.name}</h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              {country.description}
            </p>
          </div>
        </div>
      </div>

      {/* Quick Facts Bar */}
      <div className="bg-primary text-primary-foreground py-6">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div>
              <div className="text-sm opacity-90">Capital</div>
              <div className="font-semibold text-lg">{country.capital}</div>
            </div>
            <div>
              <div className="text-sm opacity-90">Language</div>
              <div className="font-semibold text-lg">{country.language}</div>
            </div>
            <div>
              <div className="text-sm opacity-90">Currency</div>
              <div className="font-semibold text-lg">{country.currencySymbol} {country.currency}</div>
            </div>
            <div>
              <div className="text-sm opacity-90">Population</div>
              <div className="font-semibold text-lg">{country.population}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        {/* Why Study Here Section */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-8 text-center">Why Study in {country.name}?</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {country.highlights.map((highlight, index) =>
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow duration-300">
              
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="CheckCircleIcon" size={24} className="text-primary" />
                  </div>
                  <p className="text-foreground font-medium">{highlight}</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Tabbed Information Section */}
        <section className="mb-16">
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            {/* Tab Navigation */}
            <div className="flex border-b border-border overflow-x-auto">
              <button
                onClick={() => setActiveTab('universities')}
                className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                activeTab === 'universities' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`
                }>
                
                Top Universities
              </button>
              <button
                onClick={() => setActiveTab('visa')}
                className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                activeTab === 'visa' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`
                }>
                
                Visa Requirements
              </button>
              <button
                onClick={() => setActiveTab('costs')}
                className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                activeTab === 'costs' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`
                }>
                
                Cost of Living
              </button>
              <button
                onClick={() => setActiveTab('scholarships')}
                className={`px-6 py-4 font-semibold transition-colors whitespace-nowrap ${
                activeTab === 'scholarships' ? 'bg-primary text-primary-foreground' : 'bg-background text-muted-foreground hover:bg-muted'}`
                }>
                
                Scholarships
              </button>
            </div>

            {/* Tab Content */}
            <div className="p-8">
              {activeTab === 'universities' &&
              <div className="space-y-6">
                  {country.universities.map((university, index) =>
                <div
                  key={index}
                  className="bg-background border border-border rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300">
                  
                      <div className="md:flex">
                        <div className="md:w-1/3 relative h-48 md:h-auto">
                          <Image
                        src={university.image}
                        alt={university.imageAlt}
                        fill
                        className="object-cover" />
                      
                        </div>
                        <div className="md:w-2/3 p-6">
                          <h3 className="text-2xl font-bold text-foreground mb-2">{university.name}</h3>
                          <p className="text-primary font-semibold mb-4">{university.ranking}</p>
                          <div className="mb-4">
                            <h4 className="font-semibold text-foreground mb-2">Popular Programs:</h4>
                            <div className="flex flex-wrap gap-2">
                              {university.programs.map((program, idx) =>
                          <span
                            key={idx}
                            className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                            
                                  {program}
                                </span>
                          )}
                            </div>
                          </div>
                          <p className="text-foreground">
                            <span className="font-semibold">Tuition:</span> {university.tuitionRange}
                          </p>
                        </div>
                      </div>
                    </div>
                )}
                </div>
              }

              {activeTab === 'visa' &&
              <div className="space-y-6">
                  {country.visaRequirements.map((visa, index) =>
                <div key={index}>
                      <h3 className="text-2xl font-bold text-foreground mb-4">{visa.type}</h3>
                      <p className="text-muted-foreground mb-4">
                        <span className="font-semibold text-foreground">Processing Time:</span> {visa.processingTime}
                      </p>
                      <h4 className="font-semibold text-foreground mb-3">Required Documents:</h4>
                      <ul className="space-y-2">
                        {visa.requirements.map((req, idx) =>
                    <li key={idx} className="flex items-start space-x-3">
                            <Icon name="CheckCircleIcon" size={20} className="text-primary flex-shrink-0 mt-1" />
                            <span className="text-foreground">{req}</span>
                          </li>
                    )}
                      </ul>
                    </div>
                )}
                </div>
              }

              {activeTab === 'costs' &&
              <div>
                  <h3 className="text-2xl font-bold text-foreground mb-6">Monthly Living Expenses</h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center py-3 border-b border-border">
                      <span className="text-foreground font-medium">Accommodation</span>
                      <span className="text-foreground font-semibold">{country.costOfLiving.accommodation}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-border">
                      <span className="text-foreground font-medium">Food & Groceries</span>
                      <span className="text-foreground font-semibold">{country.costOfLiving.food}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-border">
                      <span className="text-foreground font-medium">Transportation</span>
                      <span className="text-foreground font-semibold">{country.costOfLiving.transportation}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-border">
                      <span className="text-foreground font-medium">Utilities</span>
                      <span className="text-foreground font-semibold">{country.costOfLiving.utilities}</span>
                    </div>
                    <div className="flex justify-between items-center py-3 border-b border-border">
                      <span className="text-foreground font-medium">Entertainment</span>
                      <span className="text-foreground font-semibold">{country.costOfLiving.entertainment}</span>
                    </div>
                    <div className="flex justify-between items-center py-4 bg-primary/10 rounded-lg px-4 mt-4">
                      <span className="text-foreground font-bold text-lg">Total Estimated Cost</span>
                      <span className="text-primary font-bold text-xl">{country.costOfLiving.total}</span>
                    </div>
                  </div>
                </div>
              }

              {activeTab === 'scholarships' &&
              <div className="space-y-6">
                  {country.scholarships.map((scholarship, index) =>
                <div
                  key={index}
                  className="bg-background border border-border rounded-lg p-6 hover:shadow-lg transition-shadow duration-300">
                  
                      <h3 className="text-xl font-bold text-foreground mb-2">{scholarship.name}</h3>
                      <p className="text-primary font-semibold mb-3">{scholarship.amount}</p>
                      <p className="text-muted-foreground">
                        <span className="font-semibold text-foreground">Eligibility:</span> {scholarship.eligibility}
                      </p>
                    </div>
                )}
                </div>
              }
            </div>
          </div>
        </section>

        {/* Career Opportunities */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-8 text-center">Career Opportunities</h2>
          <div className="bg-card border border-border rounded-lg p-8">
            <ul className="space-y-4">
              {country.careerOpportunities.map((opportunity, index) =>
              <li key={index} className="flex items-start space-x-4">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="BriefcaseIcon" size={18} className="text-primary" />
                  </div>
                  <span className="text-foreground text-lg">{opportunity}</span>
                </li>
              )}
            </ul>
          </div>
        </section>

        {/* Cultural Insights */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-8 text-center">Cultural Insights</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {country.culturalInsights.map((insight, index) =>
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-6 hover:shadow-lg transition-shadow duration-300">
              
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Icon name="GlobeAltIcon" size={20} className="text-accent" />
                  </div>
                  <p className="text-foreground">{insight}</p>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Ireland Cost Calculator — shown only for Ireland */}
        {countryId === 'ireland' &&
        <section className="mb-16">
            <h2 className="text-4xl font-bold text-foreground mb-8 text-center">Study Cost Calculator</h2>
            <IrelandCostCalculator />
          </section>
        }

        {/* Student Testimonials */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-8 text-center">Student Experiences</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {country.testimonials.map((testimonial, index) =>
            <div
              key={index}
              className="bg-card border border-border rounded-lg p-8 hover:shadow-lg transition-shadow duration-300">
              
                <div className="flex items-center space-x-4 mb-4">
                  <div className="relative w-16 h-16 rounded-full overflow-hidden">
                    <Image
                    src={testimonial.image}
                    alt={testimonial.imageAlt}
                    fill
                    className="object-cover" />
                  
                  </div>
                  <div>
                    <h3 className="font-bold text-foreground">{testimonial.name}</h3>
                    <p className="text-muted-foreground text-sm">{testimonial.university}</p>
                  </div>
                </div>
                <p className="text-foreground italic">"{testimonial.quote}"</p>
              </div>
            )}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-16">
          <h2 className="text-4xl font-bold text-foreground mb-8 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4 max-w-4xl mx-auto">
            {country.faqs.map((faq, index) =>
            <div
              key={index}
              className="bg-card border border-border rounded-lg overflow-hidden">
              
                <button
                onClick={() => setExpandedFAQ(expandedFAQ === index ? null : index)}
                className="w-full px-6 py-4 flex items-center justify-between hover:bg-muted transition-colors">
                
                  <span className="font-semibold text-foreground text-left">{faq.question}</span>
                  <Icon
                  name={expandedFAQ === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={20}
                  className="text-muted-foreground flex-shrink-0 ml-4" />
                
                </button>
                {expandedFAQ === index &&
              <div className="px-6 py-4 bg-muted/50 border-t border-border">
                    <p className="text-foreground">{faq.answer}</p>
                  </div>
              }
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-gradient-to-r from-primary to-primary-foreground rounded-2xl p-12 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">Ready to Start Your Journey?</h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Book a free consultation with our expert counselors to discuss your study abroad plans for {country.name}.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              href="/consultation-booking"
              className="px-8 py-4 bg-white text-primary font-bold rounded-lg hover:bg-white/90 transition-colors text-lg">
              
              Book Free Consultation
            </Link>
            <Link
              href="/destinations"
              className="px-8 py-4 bg-primary-foreground/10 text-white border-2 border-white rounded-lg hover:bg-primary-foreground/20 transition-colors font-semibold text-lg">
              
              Explore Other Destinations
            </Link>
          </div>
        </section>
      </div>
      <Footer />
    </div>);

}